import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  limit,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Customer, CustomerRank, CustomerStatus, ID, PartnerType } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot } from '@shared/utils';
import { generateCustomerCode } from '@shared/utils';
import { writeAuditLog } from '@shared/utils';
import { DEFAULT_RANK } from '../utils/customer-rank';

const customerCreateSchema = z.object({
  name: z.string().min(1, '顧客名は必須です'),
  nameKana: z.string().min(1, 'フリガナは必須です'),
  // 新規は既定 C スタート。auto の顧客は月次締め時に取引実績から自動再計算される。
  rank: z.enum(['SSS', 'SS', 'S', 'A', 'B', 'C']).default(DEFAULT_RANK),
  rankMode: z.enum(['auto', 'manual']).default('auto'),
  status: z.enum(['active', 'inactive', 'prospect']),
  // 区分（複合可）。未指定時は顧客として扱う。
  partnerTypes: z.array(z.enum(['customer', 'supplier', 'subcontractor'])).default(['customer']),
  industry: z.string().default(''),
  // TEL・郵便番号は通常必須だが、仕入先（通販業者を含む）にチェックがある場合は
  // 末尾の superRefine で必須を解除する。
  postalCode: z.string().default(''),
  prefecture: z.string().default(''),
  phone: z.string().default(''),
  fax: z.string().nullable().default(null),
  email: z.string().email().nullable().default(null),
  website: z.string().url().nullable().default(null),
  assignedUserId: z.string().nullable().default(null),
  paymentTerms: z.number().int().min(0).default(30),
  notes: z.string().nullable().default(null),
  // Phase 5-B 追加フィールド
  parentId: z.string().nullable().default(null),
  areaId: z.string().nullable().default(null),
  // 締め日・支払日は顧客登録フォームで必須（UI側で選択を強制）。
  // データ層は仕入先/外注先の簡易登録(createPartner)や既存データを壊さないよう許容のまま。
  limitTypeId: z.string().nullable().default(null),
  paymentDateId: z.string().nullable().default(null),
  taxCalcMode: z.enum(['floor', 'ceil', 'round', 'manual']).default('floor'),
  tel2: z.string().nullable().default(null),
  group: z.string().nullable().default(null),
  // 住所の共通仕様（AIRS統一）: 市区町村 / 番地 / 建物名・フロア / 部署
  addr1: z.string().default(''),
  addr2: z.string().default(''),
  addr3: z.string().default(''),
  department: z.string().default(''),
  // AI秘書の書込ツールだけが true を渡す（本体UIのフォームはこのフィールドを送らない）。
  // 人がレビュー確定すると deleteField() で消える。docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
  aiPendingReview: z.boolean().optional(),
}).superRefine((data, ctx) => {
  // 仕入先（通販業者を含む）にチェックがある場合は TEL・郵便番号の必須を解除。
  // 仕入先にチェックがない場合は従来通り必須。
  const isSupplier = data.partnerTypes.includes('supplier');
  if (isSupplier) return;
  if (data.phone.trim() === '') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['phone'], message: '電話番号は必須です' });
  }
  if (data.postalCode.trim() === '') {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['postalCode'], message: '郵便番号は必須です' });
  }
});

export type CustomerCreateInput = z.infer<typeof customerCreateSchema>;
export type CustomerUpdateInput = Partial<Omit<CustomerCreateInput, 'rank' | 'status'>> & {
  rank?: CustomerRank;
  status?: CustomerStatus;
};

const COL = 'customers';

export async function fetchCustomers(filters?: {
  status?: CustomerStatus;
  rank?: CustomerRank;
  assignedUserId?: ID;
}): Promise<Customer[]> {
  let q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    limit(1000)
  );
  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }
  if (filters?.rank) {
    q = query(q, where('rank', '==', filters.rank));
  }
  if (filters?.assignedUserId) {
    q = query(q, where('assignedUserId', '==', filters.assignedUserId));
  }
  const snap = await getDocs(q);
  const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Customer);
  // 顧客コード（登録順の連番。ゼロ埋め文字列のため辞書順=数値昇順）で並べる。
  // これにより一覧の表示行番号(#)とコードの並びが一致する。
  return list.sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''));
}

export async function fetchCustomer(id: ID): Promise<Customer | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<Customer>(snap);
}

/**
 * 区分で絞り込んだ取引先を取得する。
 * 外注発注では subcontractor、仕入発注では supplier を渡して相手先候補を得る。
 * array-contains + orderby の複合インデックスを避けるため、並び替えはクライアント側で行う。
 */
export async function fetchCustomersByPartnerType(type: PartnerType): Promise<Customer[]> {
  const snap = await getDocs(
    query(
      collection(db, COL),
      where('isDeleted', '==', false),
      where('partnerTypes', 'array-contains', type)
    )
  );
  const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Customer);
  return list.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'ja'));
}

export interface PartnerMinimalInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  postalCode?: string | null;
  // 住所の共通仕様（AIRS統一）: 都道府県 / 市区町村 / 番地 / 建物名・フロア / 部署
  prefecture?: string;
  addr1?: string;
  addr2?: string;
  addr3?: string;
  department?: string;
  paymentTerms?: number;
  notes?: string | null;
}

/**
 * 外注/仕入の発注フォームから「その場で新規登録」するための簡易取引先作成。
 * 取引先マスタ（customers）に指定区分を付与して登録する。
 */
export async function createPartner(
  data: PartnerMinimalInput,
  partnerType: PartnerType,
  userId: ID
): Promise<Customer> {
  if (!data.name || !data.name.trim()) {
    throw new Error('取引先名は必須です');
  }
  const code = await generateCustomerCode();
  const payload = {
    name: data.name,
    nameKana: '',
    rank: DEFAULT_RANK,
    rankMode: 'auto' as const,
    rankUpdatedAt: null,
    rankStats: null,
    status: 'active' as CustomerStatus,
    partnerTypes: [partnerType],
    industry: '',
    postalCode: data.postalCode ?? '',
    prefecture: data.prefecture ?? '',
    phone: data.phone ?? '',
    fax: null,
    email: data.email ?? null,
    website: null,
    assignedUserId: null,
    paymentTerms: data.paymentTerms ?? 30,
    notes: data.notes ?? null,
    parentId: null,
    areaId: null,
    limitTypeId: null,
    paymentDateId: null,
    taxCalcMode: 'floor' as const,
    tel2: null,
    group: null,
    addr1: data.addr1 ?? '',
    addr2: data.addr2 ?? '',
    addr3: data.addr3 ?? '',
    department: data.department ?? '',
    code,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);
  await writeAuditLog({
    entityType: 'customer',
    entityId: ref.id,
    action: 'create',
    userId,
    after: { name: data.name, partnerTypes: [partnerType] },
  });
  return { id: ref.id, ...payload } as unknown as Customer;
}

export async function createCustomer(
  data: CustomerCreateInput,
  userId: ID
): Promise<Customer> {
  const validated = parseOrThrow(customerCreateSchema, data);
  const code = await generateCustomerCode();
  const payload = { ...validated, rankUpdatedAt: null, rankStats: null, code, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as Customer;
  await writeAuditLog({ entityType: 'customer', entityId: ref.id, action: 'create', userId, after: validated });
  return created;
}

export async function updateCustomer(
  id: ID,
  data: CustomerUpdateInput,
  userId: ID
): Promise<Customer> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('顧客が見つかりません。');
  const before = snap.data();
  const patch = { ...data, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({ entityType: 'customer', entityId: id, action: 'update', userId, before, after: data });
  return { id, ...before, ...patch } as unknown as Customer;
}

export async function deleteCustomer(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('顧客が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({ entityType: 'customer', entityId: id, action: 'delete', userId, before: snap.data() });
}

export interface DuplicateMatch {
  customerId: string;
  field: 'name' | 'nameKana' | 'phone' | 'code' | 'addr1';
  value: string;
}

export interface DuplicateCheckResult {
  hasDuplicate: boolean;
  matches: DuplicateMatch[];
}

type DuplicateCheckField = DuplicateMatch['field'];

interface DuplicateCheckInput {
  name?: string;
  nameKana?: string;
  phone?: string;
  code?: string;
  addr1?: string;
}

export async function checkCustomerDuplicate(
  data: DuplicateCheckInput,
  excludeId?: string
): Promise<DuplicateCheckResult> {
  const fields: DuplicateCheckField[] = ['name', 'nameKana', 'phone', 'code', 'addr1'];
  const activeFields = fields.filter((f) => {
    const v = data[f];
    return v !== undefined && v !== null && v !== '';
  });

  const queries = activeFields.map((field) =>
    getDocs(
      query(
        collection(db, COL),
        where('isDeleted', '==', false),
        where(field, '==', data[field])
      )
    )
  );

  const results = await Promise.all(queries);

  const matches: DuplicateMatch[] = [];
  results.forEach((snap, idx) => {
    const field = activeFields[idx];
    snap.docs.forEach((d) => {
      if (excludeId !== undefined && d.id === excludeId) return;
      const value = data[field] as string;
      matches.push({ customerId: d.id, field, value });
    });
  });

  return { hasDuplicate: matches.length > 0, matches };
}
